import { useQuery } from "@apollo/client";
import {
  ApplePodcastIcon,
  DeezerIcon,
  FacebookIcon,
  GooglePlayIcon,
  IHeartRadioIcon,
  InstagramIcon,
  OvercastIcon,
  PandoraIcon,
  SoundCloudIcon,
  SpotifyIcon,
  SticherIcon,
  TwitterIcon,
  YouTubeIcon,
  AmazonMusicIcon,
} from "@govlaunch/editor/src/icons/socialMedia";
import { Anchor, Box, Flex, Grid, Heading, LoadingSpinner, PageContainer, Text } from "@govlaunch/web";
import { useRouter } from "next/router";
import React, { useContext } from "react";
import RequestCookiesContext from "~/components/RequestCookiesContext";
import PodcastPageSeo from "~/components/seo/PodcastPageSeo";
import useIsMobile from "~/lib/hooks/useIsMobile";
import PodcastPageConfigQuery from "~/lib/queries/PodcastPageConfigQuery";
import {
  IPodcastPageConfigQuery,
  IPodcastPageConfigQueryVariables,
} from "~/lib/queries/__generated__/PodcastPageConfigQuery.generated";
import NavbarLayout from "~/src/components/navbar/NavbarLayout";
import decodePreviewData from "~/src/utils/decodePreviewData";
import stringOrFirst from "~/src/utils/stringOrFirst";

export default function Podcast() {
  const { data, loading } = useQuery<IPodcastPageConfigQuery, IPodcastPageConfigQueryVariables>(
    PodcastPageConfigQuery,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
    },
  );
  const router = useRouter();
  const cookies = useContext(RequestCookiesContext);
  const preview = decodePreviewData(stringOrFirst(router.query.preview), cookies);

  if (preview) {
    return <PodcastPage podcast={preview as any} />;
  }

  if (loading) {
    return (
      <NavbarLayout>
        <Flex justify="center">
          <LoadingSpinner />
        </Flex>
      </NavbarLayout>
    );
  }

  if (data && data.podcastPage && !loading) {
    return <PodcastPage podcast={data.podcastPage} />;
  }

  return null;
}

interface IPodcastPageProps {
  podcast: IPodcastPageConfigQuery["podcastPage"];
}

function PodcastPage({ podcast }: IPodcastPageProps) {
  const isMobile = useIsMobile();

  return (
    <NavbarLayout pb={0} backgroundColor={podcast.backgroundColor}>
      <PodcastPageSeo />

      <PageContainer mb={8}>
        <Heading
          fontSize={["32px", "32px", "54px"]}
          color={podcast.headerTextColor}
          maxWidth={["auto", "auto", "770px"]}
          lineHeight={[null, null, "70px"]}
        >
          {podcast.headerText}
        </Heading>

        <Text fontSize={["md", "md", "lg"]} color={podcast.descriptionColor} fontWeight={400} mt={4}>
          {podcast.description}
        </Text>

        <Box mt={8}>
          <iframe
            height={isMobile ? "700px" : "480px"}
            width="100%"
            frameBorder="no"
            scrolling="no"
            seamless={true}
            src={podcast.podcastSrc}
          />
        </Box>
        <PodcastLinks podcast={podcast} />
      </PageContainer>
    </NavbarLayout>
  );
}

interface IPodcastLinksProps {
  podcast: IPodcastPageConfigQuery["podcastPage"];
}

function PodcastLinks({ podcast }: IPodcastLinksProps) {
  const size = 30;

  return (
    <Grid gridTemplateColumns={`repeat(auto-fill, ${size}px)`} gridRowGap={2} gridColumnGap={2} mt={8}>
      {podcast.twitter && (
        <Box>
          <Anchor href={podcast.twitter} external="outside-redirect" title="Twitter">
            <TwitterIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.facebook && (
        <Box>
          <Anchor href={podcast.facebook} external="outside-redirect" title="Facebook">
            <FacebookIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.instagram && (
        <Box>
          <Anchor href={podcast.instagram} external="outside-redirect" title="Instagram">
            <InstagramIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.youtube && (
        <Box>
          <Anchor href={podcast.youtube} external="outside-redirect" title="YouTube">
            <YouTubeIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.apple && (
        <Box>
          <Anchor href={podcast.apple} external="outside-redirect" title="Apple Podcast">
            <ApplePodcastIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.spotify && (
        <Box>
          <Anchor href={podcast.spotify} external="outside-redirect" title="Spotify">
            <SpotifyIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.googleplay && (
        <Box>
          <Anchor href={podcast.googleplay} external="outside-redirect" title="Google Play">
            <GooglePlayIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.soundcloud && (
        <Box>
          <Anchor href={podcast.soundcloud} external="outside-redirect" title="SoundCloud">
            <SoundCloudIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.deezer && (
        <Box>
          <Anchor href={podcast.deezer} external="outside-redirect" title="Deezer">
            <DeezerIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.iheartradio && (
        <Box>
          <Anchor href={podcast.iheartradio} external="outside-redirect" title="iHeartRadio">
            <IHeartRadioIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.overcast && (
        <Box>
          <Anchor href={podcast.overcast} external="outside-redirect" title="Overcast">
            <OvercastIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.stitcher && (
        <Box>
          <Anchor href={podcast.stitcher} external="outside-redirect" title="Stitcher">
            <SticherIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.pandora && (
        <Box>
          <Anchor href={podcast.pandora} external="outside-redirect" title="Pandora">
            <PandoraIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}

      {podcast.amazonmusic && (
        <Box>
          <Anchor href={podcast.amazonmusic} external="outside-redirect" title="Amazon Music">
            <AmazonMusicIcon width={size} height={size} />
          </Anchor>
        </Box>
      )}
    </Grid>
  );
}
